import { closeDrawer, openDrawer } from "@/src/components/dialogs";

import {
  Admission,
  Billet,
  Hospitalisation,
  InitialExamination,
  Patient,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";

import { CompartmentCard } from "@/src/components/cards";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import StaggeredAnimation from "@/src/components/staggeredAnimationComponent";
import { getItemId, getUserId, uid } from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { getStandardConfidentiality } from "../patients/confidentialitiesHook";
import {
  HospitalisationHistoryItem
} from "../patients/hospitalisationsHistory";
import AdmissionCard from "./historique/admissionCard";
import AnesthesiologyConsultationButton from "./historique/anesthesiologyConsultationButton";
import AnesthesiologyConsultationCard from "./historique/anesthesiologyConsultationCard";
import AnesthesiologySummaryButton from "./historique/anesthesiologySummaryButton";
import AnesthesiologySummaryCard from "./historique/anesthesiologySummaryCard";
import AvisButton from "./historique/avisButton";
import AvisCard from "./historique/avisCard";
import BilletCard from "./historique/billetCard";
import ComplicationButton from "./historique/complicationButton";
import ComplicationCard from "./historique/complicationCard";
import DischargeButton from "./historique/dischargeButton";
import DischargeCard from "./historique/dischargeCard";
import EvolutionButton from "./historique/evolutionButton";
import EvolutionCard from "./historique/evolutionCard";
import InitialExaminationCard from "./historique/initialExaminationCard";
import InterventionButton from "./historique/interventionButton";
import InterventionCard from "./historique/interventionCard";
import ShiftButton from "./historique/shiftButton";
import ShiftCard from "./historique/shiftCard";
import TemporaryLeaveButton from "./historique/temporaryLeaveButton";
import TemporaryLeaveCard from "./historique/temporaryLeaveCard";
import ToxicityCard from "./historique/toxicityCard";
import TransferButton from "./historique/transferButton";
import TransferDemandCard from "./historique/transferDemandCard";
import VentilationButton from "./historique/ventilationButton";
import VentilationCard from "./historique/ventilationCard";
import { openEvolutionVisualisationDrawer } from "./openEvolutionVisualisation";

function HospitalisationEventsCompartment({
  title,
  patient,
  parent,
}: {
  title?: string;
  patient: Patient;
  parent: Hospitalisation;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "HospitalisationEvolutionCompartment",
      searchKey,
      getItemId(parent),
      getItemId(patient),
      reload,
    ],
    queryFn: () =>
      apiFetch(
        "evolutionItems",
        "loadMany",
        {
          patientId: patient.id,
          parentId: parent?.id,

        },
        "Erreur lors du chargement de l'historique"
      ),
  });

  const mandatoryItemTypes: ("billet" | "admission" | "initialExamination")[] =
    parent?.bed
      ? ["initialExamination", "billet"]
      : ["billet", "initialExamination"];

  let allItems = data || [];
  allItems = allItems.sort((a, b) => {
    return moment(a._date).diff(moment(b._date));
  });

  for (let mandatoryItemType of mandatoryItemTypes) {
    let existingItem = allItems.find((item) => item.type == mandatoryItemType);
    if (!existingItem) {
      let item = {
        type: mandatoryItemType,
        patient,
        creator: getUserId(),
        parent,
        confidentiality: getStandardConfidentiality(patient),
      } as Billet | Admission | InitialExamination;
      allItems = [item, ...allItems];
    }
  }

  async function openVisualisation(index: number) {
    await openEvolutionVisualisationDrawer({
      items: allItems!,
      index,
      patient,
    });
  }

  function close() {
    closeDrawer("historyTypeDrawer");
  }

  return (
    <CompartmentCard
      title={title || "Evènements"}
      // secondaryButtonTitle={"Hist."}
      // onSecondaryClick={async () => {
      //       openHospitalisationsHistoryDrawer(patient, parent);
      //     }}
      onClick={
        parent == null
          ? undefined
          : async () => {
              await openDrawer({
                id: "historyTypeDrawer",
                title: "Ajouter un élément",

                content: (
                  <div className="c1 gap20">
                    <EvolutionButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />

                    <ShiftButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />

                    {/* <ToxicityButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    /> */}

                    <AvisButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />

                    <InterventionButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />

                    <AnesthesiologyConsultationButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />
                    <AnesthesiologySummaryButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />

                    <ComplicationButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />
                    <VentilationButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />

                    <TransferButton
                      patient={patient}
                      parent={parent}
                      onSave={(item) => {
                        setReload((prev) => prev + 1);
                        close();
                      }}
                    />

                    {parent.bed && (
                      <TemporaryLeaveButton
                        patient={patient}
                        parent={parent}
                        onSave={(item) => {
                          setReload((prev) => prev + 1);
                          close();
                        }}
                      />
                    )}

                    {parent.bed && (
                      <DischargeButton
                        patient={patient}
                        parent={parent}
                        onSave={(item) => {
                          setReload((prev) => prev + 1);
                          close();
                        }}
                      />
                    )}
                  </div>
                ),
              });
            }
      }
    >
      <ListConstructor
        isLoading={isLoading}
        isError={isError}
        error={error}
        data={data?.length ? data : ["a"]}
      >
        <div className="grid c1 gap10">
          {/* {mandatoryItemTypes.map((type) => {
            let existingItem = allItems.find((item) => item.type == type);

            if (existingItem) return null;
            let typeInfo = evolutionItemTypes[type];

            return (
              <div
                key={uid()}
                className="card redish tcenter"
                onClick={async () => {
                  let item: Billet | Admission | InitialExamination = {
                    type,
                    patient,
                    creator: getUserId(),
                    parent,
                    functionalSigns: [],
                    physicalSigns: [],
                    readBy: [getUserId()],
                    confidentiality: getStandardConfidentiality(patient),
                  };

                  if (item.type == "billet")
                    return await openBillet({
                      item,
                      parent,
                      patient,
                      onChange: (item) => {
                        setReload((prev) => prev + 1);
                      },
                    });

                  await openEvolutionStandardForm({
                    item,
                    patient,
                    onConfirm: (newItem) => {
                      setReload((prev) => prev + 1);
                    },
                  });
                }}
              >
                <div className="bold">{typeInfo.name}</div>
                <div>Aucune donnée saisie</div>
              </div>
            );
          })} */}

          {allItems?.map((item, index) => {
            let child = <div></div>;
            let itemParent = parent;

            if (item.type === "billet")
              child = (
                <BilletCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async (item) => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {}}
                />
              );

            if (item.type == "admission")
              child = (
                <AdmissionCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            if (item.type == "initialExamination")
              child = (
                <InitialExaminationCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            if (item.type === "evolution")
              child = (
                <EvolutionCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async (item) => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            if (item.type === "shift")
              child = (
                <ShiftCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            if (item.type === "avis")
              child = (
                <AvisCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                />
              );

            if (item.type === "intervention")
              child = (
                <InterventionCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            if (item.type === "ventilation")
              child = (
                <VentilationCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            if (item.type === "transferDemand")
              child = (
                <TransferDemandCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {}}
                />
              );

            if (item.type == "temporaryLeave")
              child = (
                <TemporaryLeaveCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onClick={async () => {}}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                />
              );

            if (item.type == "discharge")
              child = (
                <DischargeCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {}}
                />
              );

            if (item.type === "anesthesiologyConsultation")
              child = (
                <AnesthesiologyConsultationCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );
            if (item.type === "anesthesiologySummary")
              child = (
                <AnesthesiologySummaryCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            if (item.type === "complication")
              child = (
                <ComplicationCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );
              
            if (item.type === "toxicity")
              child = (
                <ToxicityCard
                  parent={itemParent}
                  item={item}
                  patient={patient}
                  onChange={async () => {
                    setReload((prev) => prev + 1);
                  }}
                  onClick={async () => {
                    await openVisualisation(index);
                  }}
                />
              );

            return (
              <StaggeredAnimation key={item?.id} index={index} increment={1}>
                {child}
              </StaggeredAnimation>
            );
          })}
        </div>
      </ListConstructor>
    </CompartmentCard>
  );
}

async function openHospitalisationsHistoryDrawer(
  patient: Patient,
  currentHospitalisation: Hospitalisation
) {
  await openDrawer({
    id: "hospitalisationsHistoryDrawer",
    title: "Autres hospitalisations",
    content: (
      <div className="c1 gap10">
        <FetchableList
          queryKey={["HospitalisationsHistory", patient.id, uid()]}
          queryFn={() =>
            apiFetch("hospitalisations", "loadMany", {
              patientId: patient.id,
              bedsIds: null,
            })
          }
          emptyChild={<div>Aucune hospitalisation trouvée</div>}
        >
          {(data) => (
            <div className="grid c1 gap10">
              {data?.filter(
                (item) => getItemId(item) != getItemId(currentHospitalisation)
              ).length === 0 && <div>Aucune hospitalisation trouvée</div>}

              {data?.map((item, index) => {
                if (getItemId(item) == getItemId(currentHospitalisation))
                  return null;
                return (
                  <StaggeredAnimation key={uid()} index={index}>
                    <HospitalisationHistoryItem
                      item={item}
                      patient={patient}
                      isDeletable={true}
                      onClick={async () => {
                        closeDrawer("hospitalisationsHistoryDrawer");
                        await openDrawer({
                          id: "hospitalisationsHistoryDrawer",
                          title: `Evènements de l'hospitalisation du ${moment(
                            item._date
                          ).format("LL")}`,
                          content: (
                            <HospitalisationEventsCompartment
                              patient={patient}
                              parent={item}
                            />
                          ),
                        });
                      }}
                      onConfirm={(item) => {
                        closeDrawer("historyTypeDrawer");
                      }}
                    />
                  </StaggeredAnimation>
                );
              })}
            </div>
          )}
        </FetchableList>
      </div>
    ),
  });
}

export { HospitalisationEventsCompartment, openEvolutionVisualisationDrawer };
